import { has, map } from "lodash";
import { Box, Divider, Flex, H3, TextInput } from "orcs-design-system";
import { type ReactNode } from "react";
import styled from "styled-components";

import { TAG_ATTRIBUTE_ACTION } from "src/consts/tags";
import type { TagConfigAttribute } from "src/types/tagConfigAttributes";

import TagAttributeInput from "./TagAttributeInput";
import { getLabel } from "./TagAttributeModal.logic";

export const AdjustedTextInput = styled(TextInput)`
  height: 38px;
`;

export type TagAttributesFieldsProps = {
  attrTypeValues: readonly TagConfigAttribute[];
  updatedAttributes: Record<string, unknown>;
  onAttrValueChange: (id: string, isEntityTagAttr?: boolean) => unknown;
  onSelectAttrValue: (id: string, isEntityTagAttr?: boolean) => unknown;
  onDateChange: (id: string, isEntityTagAttr?: boolean) => unknown;
  fieldToBeFocused?: { id?: string } | undefined;
  fieldsToShowInvalidity?: Record<string, unknown> | undefined;
  featureFlags?: unknown;
  tagTypesConfig?: unknown;
  editMode?: boolean | undefined;
};

const TagAttributesFields = ({
  attrTypeValues,
  updatedAttributes,
  onAttrValueChange,
  onSelectAttrValue,
  onDateChange,
  fieldToBeFocused,
  fieldsToShowInvalidity = {},
  featureFlags,
  tagTypesConfig,
  editMode = false,
}: TagAttributesFieldsProps) => {
  return (
    <Flex flexDirection="column" gap="r">
      {map(attrTypeValues, (attrType) => {
        const {
          id,
          associatedValue,
          defaultValue,
          action,
          type,
          label = "",
          isEntityTagAttr,
        } = attrType;

        let value = defaultValue;
        if (has(updatedAttributes, id)) {
          value = updatedAttributes[id];
        }
        const isReadOnly =
          action === TAG_ATTRIBUTE_ACTION.READONLY || !editMode;
        const isMandatory =
          action === TAG_ATTRIBUTE_ACTION.EDITABLE ||
          action === TAG_ATTRIBUTE_ACTION.MANDATORY;

        return (
          <TagAttributeInput
            key={id}
            type={type}
            id={id}
            data-testid={associatedValue}
            label={getLabel(label, isReadOnly)}
            placeholder="Not set"
            fullWidth
            value={value}
            InputComponent={AdjustedTextInput}
            associatedValue={associatedValue}
            resolvedTeamTypeIds={attrType.resolvedTeamTypeIds}
            disabled={isReadOnly}
            mandatory={isMandatory}
            onChange={onAttrValueChange(id, isEntityTagAttr)}
            onSelectAttrValue={onSelectAttrValue(id, isEntityTagAttr)}
            onDateChange={onDateChange(id, isEntityTagAttr)}
            invalid={fieldsToShowInvalidity[id]}
            focus={fieldToBeFocused?.id === id}
            featureFlags={featureFlags}
            tagTypesConfig={tagTypesConfig}
          />
        );
      })}
    </Flex>
  );
};

export const UniqueAttributesSection = ({
  extraNotice,
  ...fieldsProps
}: TagAttributesFieldsProps & { extraNotice?: ReactNode }) => (
  <Flex width="100%" p="xs" pt="r" flexDirection="column">
    <Divider light />
    <Box width="50%" mt="r">
      <Box mb="r">
        <H3>Unique attributes</H3>
        Note: Changing these will update these values only for this entity.
      </Box>
      {extraNotice}
      <TagAttributesFields {...fieldsProps} />
    </Box>
  </Flex>
);

export const ParentTagsSection = (fieldsProps: TagAttributesFieldsProps) => (
  <Flex width="100%" p="xs" pt="r" flexDirection="column">
    <Divider light />
    <Box width="50%" mt="r">
      <Box mb="r">
        <H3>Parent tags</H3>
      </Box>
      <TagAttributesFields {...fieldsProps} />
    </Box>
  </Flex>
);

export default TagAttributesFields;
